<!-- eslint-disable vue/attribute-hyphenation -->
<template>
  <div data-app>
    <div class="form-group search">
      <label for="search_forums">
        Search {{ indexType }} sites:
      </label>
      <div class="input-btn-group search-with-btn">
        <div class="search-bar">
          <autocomplete-input
              inputId="search"
              :initial-value="query"
              :suggestions="siteNames"
              @getSuggestions="getSiteNameSuggestions"
              @submitInput="searchSites"
              @setInput="(newQuery) => query = newQuery"
          />
          <button
            ref="searchBtn"
            class="btn btn--primary btn--inline"
            @click.prevent="searchSites"
          >
            <img
              src="/static/images/icons/icon-search-white.svg"
              width="24"
              alt="Search"
              style="display:block"
            >
          </button>
        </div>
      </div>
    </div>
    <pagination-component
      itemName="Site"
      :loading="loading"
      :totalItems="totalSites"
      :initialParams="initialParams"
      :sortOptions="sortOptions"
      :sortUpdate="sortUpdate"
      :showNavOptions="true"
      :syncPaginationControls="performPaginationSync"
      @syncPaginationControls="syncPagination"
      @performSearch="fetchSiteIndices"
    />
    <div
      v-if="loading"
      class="alert alert--info u-text-centre"
    >
      {{ gettext('Loading...') }}
    </div>
    <div
      v-if="!loading && totalSites > 0"
      class="site-index"
    >
      <notes-legend />
      <ul
        id="search-results"
      >
        <li
          v-for="(site, index) in siteData"
          :key="index"
          class="site-index__row"
        >
          <p class="site-index__name">
            <a :href="siteUrl(site.site_name)">{{ site.site_name }}</a>
            <note-buttons
              :noteId="site.site_id"
              :noteName="site.site_name"
              :noteLinked="site.uuid"
              :noteItem="siteType"
              :noteSiteId="site.site_id"
              :noteSiteName="site.site_name"
              :hasNotes="site.has_site_notes"
              :indexType="indexType"
              :siteLink="getSiteLink(site.site_name, siteType)"
              :watchlistMessage="site"
              @refresh="fetchSiteIndices()"
            />
          </p>
          <div class="site-index__icon">
            <!-- Brian suggested and approved the removal of the url check for chats -->
            <!-- Under the assumption that all sites that show up here are onion -->
            <img
              v-if="(indexType === 'chat') || (site.url && site.url.includes('.onion'))"
              width="50"
              src="/images/logos/onion.png"
              title="This is a site found on the Tor network"
              alt=".onion site"
            >
          </div>
          <div class="site-index__info">
            <i class="u-fontsize-sm">
              Last message:
            </i>
            <br>
            {{ formatDate(site.posted_date) }}
          </div>
        </li>
      </ul>
    </div>
    <div
      v-if="!loading && totalSites === 0"
    >
      <p class="alert alert--info">
        No site data available
      </p>
    </div>

    <pagination-component
      itemName="Site"
      :loading="loading"
      :isPrimary="false"
      :totalItems="totalSites"
      :syncPaginationControls="performPaginationSync"
      @syncPaginationControls="syncPagination"
      @performSearch="fetchSiteIndices"
    />
  </div>
</template>

<script>
import AutocompleteInput from './AutocompleteInput'
import NoteButtons from './notes/NoteButtons'
import NotesLegend from './notes/NotesLegend'
import PaginationComponent from './PaginationComponent'
import axios from 'axios'
import Cookies from 'js-cookie'

export default {
  name: 'SiteIndex',
  components: {
    AutocompleteInput,
    NoteButtons,
    NotesLegend,
    PaginationComponent,
  },
  props: {
    siteType: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      siteData: [],
      siteNames: [],
      site_data_url: '',
      siteLink: null,
      sortUpdate: null,
      loading: true,
      totalSites: 0,
      initialParams: {
        start: 0,
        limit: 25,
        sort: '-posted_date'
      },
      query: '',
      sortOptions: {
        '-posted_date': 'Most Recent', // '-' means desc
        '+site_name': 'Alphabetical', // '+' means asc
      },
      performPaginationSync: false,
    }
  },
  computed: {
    indexType() {
      if (this.siteType === 'site') {
        return 'forum'
      } else {
        return 'chat'
      }
    },
    siteIndexURL() {
      return `/en/indices/site_index/${this.indexType}/`
    }
  },
  mounted() {
    // data retrieval request is emitted by pagination component
    let searchParams = new URLSearchParams(window.location.search)
    if (searchParams.get('query') != null && searchParams.get('query') !== '') {
      this.query = searchParams.get('query')
      this.sortOptions['+relevance'] = 'Relevance'
    }
  },
  methods: {
    syncPagination(performSync) {
      this.$nextTick(() => {
        this.performPaginationSync = performSync
      })
    },
    getSiteLink(siteName, siteType) {
      if (siteType.toLowerCase() === 'chat site') {
        return new URL('/en/message-search/chat-sites/' + siteName + '/', document.baseURI).href
      } else {
        return new URL('/en/message-search/forum-sites/' + siteName + '/', document.baseURI).href
      }
    },
    siteUrl(url) {
      return window.location.origin + window.location.pathname + url
    },
    formatDate(date) {
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }
      return new Date(date * 1000).toLocaleString('en-US', options)
    },
    fetchSiteIndices() {
      this.loading = true

      let searchParams = new URLSearchParams(window.location.search)
      // unfortunately we need to convert search params object into a regular js
      // object so it may be passed into request
      let requestParams = {}
      for (const [key, value] of searchParams) {
        requestParams[key] = value
      }

      const csrftoken = Cookies.get('csrftoken')
      axios.get(this.siteIndexURL, {headers: {'X-CSRFToken': csrftoken}, params: requestParams})
        .then(response => {
          this.siteData = response.data.data
          this.totalSites = response.data.total
        })
        .catch(error => {
          console.log(error)
          this.siteData = []
          this.totalSites = 0
        }).finally(() => {
          this.loading = false
        })
    },
    getSiteNameSuggestions(term) {
      this.query = term
      axios.get(
        '/en/message-search/get_site_names/',
        {'params': {'query': this.query, 'index': this.indexType === 'chat' ? 'chat_messages' : 'forum_posts'}}
      ).then((response) => {
        this.siteNames = response.data
      })
    },
    searchSites() {
      let url = new URL(window.location)
      url.searchParams.set('start', 0)
      url.searchParams.set('query', this.query)
      if (this.query != null && this.query !== '') {
        this.sortOptions['+relevance'] = 'Relevance'
        url.searchParams.set('sort', '+relevance')
        this.sortUpdate = '+relevance'
      } else {
        if (url.searchParams.get('sort') === '+relevance') {
          url.searchParams.set('sort', '-posted_date')
          this.sortUpdate = '-posted_date'
        }
        delete this.sortOptions['+relevance']
      }
      window.history.pushState({}, '', url)
      this.fetchSiteIndices()
      this.performPaginationSync = true
    }
  },
}
</script>

<style scoped>
.search-with-btn button {
  vertical-align: top;
  padding-top: 0.4em;
  padding-bottom: 0.4em;
  border: 2px solid #c22c3f;
}
.search-bar {
  display: flex;
  justify-items: stretch;
  gap: 5px;
  .autocomplete {
    width: calc(100% - 78px);
  }
}
.search {
  margin-top: 1.25rem;
  max-width: 50rem;
  margin-left: auto;
  margin-right: auto;
}
</style>